import { Form } from '@unform/web';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiLock, FiLogIn, FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import getValidationErrors from '../../utils/getValidationErrors';
import loadingGif from "../../assets/img/loadingMusic.gif";
import { useConfirmUserMutation, useLoginMutation } from '../../graphql/generated/graphql';
import { apolloClient } from '../../services/apollo';
import * as S from './styles';

export default function SignIn() {
  const formRef = useRef(null);

  const [disable, setDisable] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { addToast } = useToast();
  const { setAuthFromGraphQL } = useAuth();

  const { token } = useParams();
  const navigate = useNavigate();

  const [confirmUser] = useConfirmUserMutation();
  const [login] = useLoginMutation();

  useEffect(() => {
    delete api.defaults.headers.authorization;

    localStorage.removeItem('@AcordeOn:token');
    localStorage.removeItem('@AcordeOn:refreshToken');
    localStorage.removeItem('@AcordeOn:user');

    // Limpa o cache do Apollo para garantir que dados do usuário anterior não persistam
    apolloClient.clearStore();

    if (token) {
      confirmUser({
        variables: {
          data: {
            tokenId: token,
          },
        },
      })
        .then(response => {
          const { user, message } = response.data.confirmUser;

          addToast({
            type: 'success',
            title: message || 'Confirmação realizada!',
            description: user.email
              ? `Faça login com o e-mail ${user.email} e sua senha`
              : 'Faça login com seu e-mail e senha',
          });
        })
        .catch(error => {
          let errorMessage = 'Erro ao confirmar usuário';

          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            errorMessage = error.graphQLErrors[0].message;
          } else if (error.networkError) {
            errorMessage = 'Serviço indisponível no momento';
          }

          addToast({
            type: 'error',
            title: 'Erro',
            description: errorMessage,
          });
        });
    }
  }, [addToast, token, confirmUser]);

  const handleSubmit = useCallback(
    async data => {
      try {
        formRef.current.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('E-mail obrigatório')
            .email('Insira um e-mail válido'),
          password: Yup.string().required('Senha obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setDisable(disable === '' ? 'active' : '');
        setLoading(true);

        const response = await login({
          variables: {
            data: {
              email: data.email.toLowerCase(),
              password: data.password,
            },
          },
        });

        const loginResult = response.data?.login;

        if (loginResult?.__typename === 'LoginSuccessResponse') {
          const { accessToken, refreshToken, user } = loginResult;

          // Atualiza o estado do AuthContext e salva no localStorage
          setAuthFromGraphQL({ accessToken, refreshToken, user });

          sessionStorage.setItem('@AcordeOn:showLoginBanner', 'true');

          setLoading(false);
          setDisable('');

          navigate('/dashboard/inicio/resumo');
        } else if (loginResult?.__typename === 'LoginErrorResponse') {
          setLoading(false);
          setDisable('');

          addToast({
            type: 'error',
            title: 'Erro na autenticação',
            description: loginResult.message,
          });
        }
      } catch (error) {
        setLoading(false);
        setDisable(disable === 'active' ? '' : '');

        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current.setErrors(errors);
          return;
        }

        // Tratamento de erros GraphQL
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const errorMessage = error.graphQLErrors[0].message;
          addToast({
            type: 'error',
            title: 'Erro na autenticação',
            description: errorMessage,
          });
          return;
        }

        if (error.networkError) {
          addToast({
            type: 'error',
            title: 'Erro na autenticação',
            description:
              'Serviço de dados indisponível no momento, favor tente mais tarde!',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na autenticação',
          description: 'Ocorreu um erro ao tentar fazer login',
        });
      }
    },
    [disable, login, addToast, navigate, setAuthFromGraphQL]
  );

  return (
    <S.BackgroundLogin>
        <S.BackgroundSectionLeftLogin>
          <S.DivLeftLogin>
            <span>Crie uma conta e registre sua música por apenas R$ 19,90</span>
            <Link to="/cadastro" aria-label="Criar conta">Criar conta</Link>
          </S.DivLeftLogin>
        </S.BackgroundSectionLeftLogin>
        <S.BackgroundSectionRightLogin>
          <S.DivRightLogin>
            <span>Acesse</span>
            <p>Sua conta do Acordeon</p>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <Input
                icon={FiMail}
                name="email"
                type="email"
                placeholder="Email"
                textTranform="lowercase"
              />
              <Input
                icon={FiLock}
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                iconView={showPassword ? FiEyeOff : FiEye}
                handleViewPassword={() => setShowPassword(prev => !prev)}
              />

              <Link to="/redefinir-senha">Esqueceu a senha?</Link>

              <Button type="submit" disabled={loading} className={disable}>
                Entrar
              </Button>
              {loading && <img src={loadingGif} alt="Carregando..." width="50px" />}
            </Form>
            <Link to="/cadastro" aria-label="Criar conta" className="returnCadaster">
              <FiLogIn /> Criar conta
            </Link>
          </S.DivRightLogin>
        </S.BackgroundSectionRightLogin>
    </S.BackgroundLogin>
  );
}
